import { Component, OnInit, Inject, LOCALE_ID } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { NgForm } from "@angular/forms";

import { User } from "../../shared/models/user.interface";
import { CustomerType } from "../../shared/models/customer.type.interface";
import { Customer } from "../../shared/models/customer.interface";
import { MonthType } from "../../shared/models/month.type.interface";
import { JobType } from "../../shared/models/job.type.interface";
import { QuoteService } from "../../quote/services/quote.service";
import { LostQuote, LostQuoteResult } from "../models/lost.quote.interface";
import { ReportService } from "../services/report.service";
import { FileService } from "../../shared/services/file.service";
import { AbstractBiditComponent } from "../../shared/controls/abstract.bidit.component";
import { DeviceDetectorService } from "ngx-device-detector";
import { AbstractReportComponent } from "../abstract.report.component";
import { RolePermission } from "../../shared/models/role.permission.enum";

export interface LostQuoteParameterData {
  lostQuote: LostQuote;

  salespersons: User[];
  jobTypes: JobType[];
  customerTypes: CustomerType[];
  customers: Customer[];
  years: number[];
  months: MonthType[];
}

@Component({
  selector: 'app-lost-quote',
  templateUrl: './lost.quote.component.html',
  styleUrls: ['./lost.quote.component.scss']
})
export class LostQuoteComponent extends AbstractReportComponent implements OnInit {

  report: LostQuoteResult = null;
  parameters: LostQuote = {
    salespersonId: null,
    jobType_ID: null,
    customerType_ID: null,
    customer_ID: null,
    year: (new Date()).getFullYear(),
    startMonth: 0,
    endMonth: 11
  } as LostQuote;

  salesperson: User;
  jobType: JobType;
  customerType: CustomerType;
  customer: Customer;

  salespersons: User[];
  jobTypes: JobType[];
  customerTypes: CustomerType[];
  customers: Customer[];
  showMargins: boolean = false;

  constructor(@Inject(LOCALE_ID) locale: string,
    deviceDetectorService: DeviceDetectorService,
    dialog: MatDialog, private quoteService: QuoteService, private reportService: ReportService, private fileService: FileService) {
    super(locale, dialog, deviceDetectorService);
  }

  ngOnInit() {
    this.showMargins = QuoteService.hasPermission(RolePermission.ViewEditMargins);

    this.quoteService.getCustomerTypes()
      .subscribe({
        next: (data) => {
          this.customerTypes = data;
          this.showParameters();
        },
        error: (error: string) => this.onError(error)
      });

    this.quoteService.getJobTypes()
      .subscribe({
        next: (data) => {
          this.jobTypes = data;
          this.showParameters();
        },
        error: (error: string) => this.onError(error)
      });

    this.quoteService.getSalespersons()
      .subscribe({
        next: (data) => {
          this.salespersons = data;
          this.showParameters();
        },
        error: (error: string) => this.onError(error)
      });

    this.quoteService.getCustomers()
      .subscribe({
        next: (data) => {
          this.customers = data;
          this.showParameters();
        },
        error: (error: string) => this.onError(error)
      });

    this.setupDates();
  }

  renderReport() {
    this.getReportFormat()
      .subscribe(formatData => {
        if (formatData == null) return;

        this.parameters.showMargins = this.showMargins;
        this.isRequesting = true;
        this.reportService.getLostQuoteExport(this.parameters, formatData.format, formatData.locale)
          .subscribe({
            next: (data) => {
              this.isRequesting = false;
              this.fileService.downloadFile(data, 'LostQuotes' + formatData.extension);
            },
            error: (error: string) => this.onError(error)
          });
      });
  }

  showParameters() {
    // wait until dropdowns loaded
    if (this.salespersons == null ||
      this.jobTypes == null ||
      this.customerTypes == null ||
      this.customers == null)
      return;

    const dialogRef = this.dialog.open(LostQuoteParameterDialog, {
      width: '550px',
      data: {
        lostQuote: (<any>Object).assign({}, this.parameters),

        salespersons: this.salespersons,
        jobTypes: this.jobTypes,
        customerTypes: this.customerTypes,
        customers: this.customers,
        years: this.years,
        months: this.months
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.parameters = result.lostQuote as LostQuote;
        this.parameters.showMargins = this.showMargins;
        this.reportService.setupParameters(this.parameters);

        this.salesperson = this.salespersons.find(s => s.id == this.parameters.salespersonId);
        this.jobType = this.jobTypes.find(s => s.jobType_ID == this.parameters.jobType_ID);
        this.customerType = this.customerTypes.find(s => s.customerType_ID == this.parameters.customerType_ID);
        this.customer = this.customers.find(s => s.customer_ID == this.parameters.customer_ID);

        this.reportService.getLostQuoteReport(result.lostQuote)
          .subscribe({
            next: (data) => {
              this.report = data;
            },
            error: (error: string) => this.onError(error)
          });
      }
    });
  }
}

@Component({
  selector: 'lost.quote.parameter.dialog',
  templateUrl: 'lost.quote.parameter.dialog.html',
})
export class LostQuoteParameterDialog {

  constructor(
    public dialogRef: MatDialogRef<LostQuoteParameterDialog>,
    @Inject(MAT_DIALOG_DATA) public data: LostQuoteParameterData) { }

  runReport(form: NgForm): void {
    if (form.valid) {
      this.dialogRef.close(this.data);
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
